import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { Box, Typography, Button, Stack, useMobileBreakpoint, useBreakpoint } from '@customink/pigment-react';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import AugustTShirtBannerImage from '~/assets/images/augustBanner.png';
import { useSignalmizelyFlagEnabled } from '~/adapters/signalmizely/hooks';
import { useEffect } from 'react';
import { addDataLayerEvent } from '~/adapters/tracking/tracking';

const BannerContainer = styled(Box)(({ theme }) => ({
    padding: `0px ${theme.spacing(4)}`,
    position: 'relative',
    minHeight: '120px',
    display: 'flex',
    [theme.breakpoints.down('lg')]: {
        marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('md')]: {
        padding: `0px ${theme.spacing(2)}`,
        minHeight: '96px',
    },
}));

const BannerImage = styled('img')(() => ({
    borderRadius: '8px',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'fill',
    zIndex: 1,
}));

const BannerContent = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    zIndex: 2,
    width: '100%',
    flex: 1,
    justifyContent: 'space-between',
    justifyItems: 'center',
    alignItems: 'center',
}));

const BannerTypography = styled(Typography)(() => ({
    paddingTop: '0px',
}));

// these should really be in optimizely as variables
const PERCENTAGE_OFF = 15;
const END_DATE = new Date(2025, 8, 1);
const PROMOTION_ID = 460;
const SHOP_NOW_URL = `https://www.customink.com/products/t-shirts/short-sleeve-t-shirts/${PROMOTION_ID}`;
const FORMATTED_END_DATE = END_DATE.toLocaleString('en-us', { month: 'long', day: 'numeric' });
const EVENT_ECOMMERCE_DATA = {
    creative_name: 'My Designs',
    creative_slot: 'Accounts Banner',
    promotion_id: PROMOTION_ID,
    promotion_name: 'August 2025 Tshirt Promo',
};

export const AugustTShirtBanner = () => {
    const isAugustTShirtBannerEnabled = useSignalmizelyFlagEnabled('sitewide_august_tshirts_promo');
    const isMediumBreakpoint = useBreakpoint('md');
    // have to set as variables to ensure consistent hook order
    const isLargeBreakpoint = useBreakpoint('lg');
    const isXLargeBreakpoint = useBreakpoint('xl');
    const renderLargeBanner = isLargeBreakpoint || isXLargeBreakpoint;
    const isMobile = useMobileBreakpoint();

    useEffect(() => {
        if (isAugustTShirtBannerEnabled) {
            addDataLayerEvent({
                event: 'view_promotion',
                ecommerce: EVENT_ECOMMERCE_DATA,
            });
        }
    }, [isAugustTShirtBannerEnabled]);

    if (!isAugustTShirtBannerEnabled) {
        return null;
    }

    const handleShopNowClick = () => {
        addDataLayerEvent({
            event: 'select_promotion',
            ecommerce: EVENT_ECOMMERCE_DATA,
        });
        return true;
    };
    return (
        <BannerContainer>
            <BannerImage src={AugustTShirtBannerImage} alt="August T-Shirt Banner" />
            <BannerContent gap={1}>
                {renderLargeBanner ? (
                    <BannerTypography variant="heading1" fontWeight="bold" color="common.white">
                        <Box
                            component="span"
                            sx={{ color: designTokens.color.yellow.default.value, marginRight: '12px' }}
                        >
                            Save {PERCENTAGE_OFF}% on Custom Tees.
                        </Box>
                        Fall into Style! Now Through {FORMATTED_END_DATE}.*
                    </BannerTypography>
                ) : (
                    <Stack direction="column">
                        <BannerTypography
                            variant={isMediumBreakpoint ? 'heading3' : 'heading5'}
                            fontWeight="bold"
                            sx={{ color: designTokens.color.yellow.default.value }}
                        >
                            Save {PERCENTAGE_OFF}% on Custom Tees.
                        </BannerTypography>
                        <BannerTypography
                            variant={isMediumBreakpoint ? 'heading3' : 'heading6'}
                            fontWeight="bold"
                            color="common.white"
                        >
                            Fall into Style! Now Through {FORMATTED_END_DATE}.*
                        </BannerTypography>
                    </Stack>
                )}
                <Button
                    size={isMobile ? 'small' : 'large'}
                    sx={{ flexShrink: 0 }}
                    onClick={handleShopNowClick}
                    href={SHOP_NOW_URL}
                >
                    Shop Now
                </Button>
            </BannerContent>
        </BannerContainer>
    );
};
